import { uuid } from 'vue-uuid';
import { type NetworkGroup, StudyGroupModel } from './groups';
import studyGroupService from '@/services/studyGroupService';
import type { FormModel } from './form';

export interface UserCacheModel {
    identifier: string;
    email: string;
    uri: string;
    user: string;
    data: string;
    avatar?: string;
    name?: string;
    b64Identifier?: string;
}

export interface UserUpdatePayloadModel {
  name?: string;
  avatar?: string;
}

export interface NetworkUser {
    identifier?: string;
    email: string;
    name: string;
    avatar: string;
    organizationId: string;
    permissionsList: string[];
    permissions: IUserPermissions;
    formsPermissions?: string[];
    editFormsPermissions?: string[];
    editForms?: string[]; // backward compatibility
    forms?: string[]; // backward compatibility
    creation: string;
    status?: string;
    inviteIdentifier?: string;
    userId?: string;
    notificationProfile?: string;
    groups?: NetworkGroup[];
}

export interface NetworkStudyUsers {
    users: NetworkUser[];
    usersCount: number;
}

interface IUser {
    identifier: string;
    email: string;
    name: string;
    avatar: string;
    organizationId: string;
    permissionsList?: string[];
    permissions: IUserPermissions;
    formsPermissions?: string[];
    editFormsPermissions?: string[];
    status?: string;
    b64Identifier?: string;
    notificationProfile?: string;
    groups?: StudyGroupModel[];
}

export interface IUserPermissions {
  viewEntries: boolean;
  addEntries: boolean;
  export: boolean;
  manageInvest: boolean;
  editCases: boolean;
  createStudy: boolean;
  forms: boolean | string[];
  editForms: boolean | string[];
  notificationProfile?: string;
  groupIdentifiers?: string[];

  studyId?: string;
}

export class UserModel {
  identifier: string;
  email: string;
  name: string;
  avatar: string;
  organizationIdentifier: string;
  permissionsList?: string[];
  permissions: IUserPermissions;
  formsPermissions?: string[];
  editFormsPermissions?: string[];
  status?: string;
  b64Identifier?: string;

  invalidEmail?: boolean;
  showDelete?: boolean;
  notificationProfile?: string;
  groups?: StudyGroupModel[];

  constructor({
    identifier,
    email,
    name,
    avatar,
    organizationId,
    permissions,
    status,
    formsPermissions,
    editFormsPermissions,
    permissionsList,
    b64Identifier,
    notificationProfile,
    groups
  } : IUser) {
    this.identifier = identifier;
    this.email = email;
    this.name = name;
    this.avatar = avatar;
    this.organizationIdentifier = organizationId;
    this.permissions = permissions;
    this.status = status;
    this.formsPermissions = formsPermissions;
    this.editFormsPermissions = editFormsPermissions;
    this.permissionsList = permissionsList;
    this.b64Identifier = b64Identifier;
    this.notificationProfile = notificationProfile;
    this.groups = groups;
  }

  static mapFromBackend(networkUser: NetworkUser, identifier: string = '', forms: FormModel[]) {
    return new UserModel({
      identifier: networkUser.identifier ? networkUser.identifier : identifier,
      email: networkUser.email,
      name: networkUser.name,
      avatar: networkUser.avatar,
      organizationId: networkUser.organizationId,
      permissions: networkUser.permissions,
      status: networkUser.status,
      formsPermissions: networkUser.formsPermissions ?? networkUser.forms,
      editFormsPermissions: networkUser.editFormsPermissions ?? networkUser.editForms,
      permissionsList: networkUser.permissionsList,
      b64Identifier: networkUser.userId,
      notificationProfile: networkUser.notificationProfile,
      groups: networkUser.groups?.map(group => {
        studyGroupService.parseFormsPermissions(group, forms);
        return StudyGroupModel.mapFromBackend(group);
      })
    });
  }

  static createEmpty(allForms: string[], showDelete?: boolean) {
    const firstUserPermissions: IUserPermissions = {
      viewEntries: true,
      addEntries: true,
      export: false,
      manageInvest: false,
      editCases: false,
      createStudy: false,
      forms: true,
      editForms: true
    };
    const invite = new UserModel({
      identifier: uuid.v4(),
      email: '',
      name: '',
      avatar: '',
      organizationId: '',
      permissions: firstUserPermissions,
      status: '',
      formsPermissions: allForms,
      editFormsPermissions: allForms,
      permissionsList: []
    });
    invite.showDelete = showDelete;
    return invite;
  }
}
